import React, { useEffect, useState } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { Button, Card, CardContent, CardActions, Typography, TextField, Grid, Switch } from '@mui/material';
import { styled } from '@mui/system';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Custom icon for markers
const markerIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41]
});

// Styled components
const StyledCard = styled(Card)(({ theme, color }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(2),
  boxShadow: theme.shadows[5],
  borderRadius: '10px',
  backgroundColor: color || theme.palette.background.paper,
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const EditHospital = ({ setMessage, onClose }) => {
  const [hospitalData, setHospitalData] = useState([]);
  const [isEditing, setIsEditing] = useState({});
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    const fetchHospitalData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'hvac', 'GenericData', 'HospitalData'));
        const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setHospitalData(data);
      } catch (error) {
        console.error('Error fetching hospital data:', error);
        setMessage('Error fetching hospital data. Please try again.');
      }
    };

    fetchHospitalData();
  }, [setMessage]);

  const handleUpdate = async (id) => {
    try {
      const hospitalRef = doc(db, 'hvac', 'GenericData', 'HospitalData', id);
      await updateDoc(hospitalRef, editValues[id]);
      setMessage('Hospital updated successfully!');
      setIsEditing(prev => ({ ...prev, [id]: false }));
    } catch (error) {
      console.error('Error updating hospital:', error);
      setMessage('Error updating hospital. Please try again.');
    }
  };

  const toggleEdit = (id) => {
    setIsEditing(prev => ({ ...prev, [id]: !prev[id] }));
    if (!isEditing[id]) {
      setEditValues(prev => ({ ...prev, [id]: { ...hospitalData.find(h => h.id === id) } }));
    }
  };

  return (
    <div>
      <Typography variant="h4" style={{ marginBottom: '20px' }}>Edit Hospitals</Typography>
      <Grid container spacing={2}>
        {hospitalData.map(hospital => (
          <Grid item xs={12} sm={6} md={4} key={hospital.id}>
            <StyledCard color={hospital.bikurCholim ? '#c8e6c9' : '#ffcdd2'}>
              <CardContent>
                <Typography variant="h5">{hospital.name}</Typography>
                <Typography variant="body1">MIEMSS ID: {hospital.miemssID}</Typography>
                <Typography variant="body1">EMS Room Code: {hospital.emsCode}</Typography>
                <Typography variant="body1">Longitude: {hospital.longitude}</Typography>
                <Typography variant="body1">Latitude: {hospital.latitude}</Typography>
                <Typography variant="body1">Phone: {hospital.phone}</Typography>
                <Typography variant="body1">Bikur Cholim: {hospital.bikurCholim ? '✓' : '✗'}</Typography>
                
                {/* Leaflet Map Preview */}
                {hospital.latitude && hospital.longitude && (
                  <div style={{ marginTop: '10px', height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                    <MapContainer
                      center={[hospital.latitude, hospital.longitude]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <Marker position={[hospital.latitude, hospital.longitude]} icon={markerIcon}>
                        <Popup>{hospital.name}</Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                )}

                {isEditing[hospital.id] && (
                  <>
                    <TextField
                      label="MIEMSS ID"
                      value={editValues[hospital.id]?.miemssID || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, [hospital.id]: { ...prev[hospital.id], miemssID: e.target.value } }))} 
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Longitude"
                      value={editValues[hospital.id]?.longitude || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, [hospital.id]: { ...prev[hospital.id], longitude: e.target.value } }))} 
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Latitude"
                      value={editValues[hospital.id]?.latitude || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, [hospital.id]: { ...prev[hospital.id], latitude: e.target.value } }))} 
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Phone"
                      value={editValues[hospital.id]?.phone || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, [hospital.id]: { ...prev[hospital.id], phone: e.target.value } }))} 
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Specialty"
                      value={editValues[hospital.id]?.specialty || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, [hospital.id]: { ...prev[hospital.id], specialty: e.target.value } }))} 
                      fullWidth
                      margin="normal"
                    />
                        
                    <TextField
                      label="EMS Room Code"
                      value={editValues[hospital.id]?.emsCode || ''}
                      onChange={(e) => setEditValues(prev => ({...prev, [hospital.id]: {...prev[hospital.id], emsCode: e.target.value}}))}
                      fullWidth
                      margin="normal"
                    />
                    <Switch
                      checked={editValues[hospital.id]?.bikurCholim || false}
                      onChange={() => setEditValues(prev => ({ ...prev, [hospital.id]: { ...prev[hospital.id], bikurCholim: !prev[hospital.id]?.bikurCholim } }))} 
                    />
                    <Typography variant="body2">Bikur Cholim</Typography>
                  </>
                )}
              </CardContent>
              <CardActions>
                {isEditing[hospital.id] ? (
                  <Button onClick={() => handleUpdate(hospital.id)} color="primary">Save</Button>
                ) : (
                  <Button onClick={() => toggleEdit(hospital.id)} color="primary">Edit</Button>
                )}
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
      <Button onClick={onClose} style={{ marginTop: '20px' }}>Close</Button>
    </div>
  );
};

export default EditHospital;
